export interface Device {
  id: number;
  serialNumber: string;
  firmwareVersion: string;
  nipperVersion: string;
  nipperCell: number;
  isConnected: boolean | null;
  brand: number;
  region: DeviceRegion | number;
  poolSize: number;
  updatedAt: string;
  createdAt: string;
  io: string;
  inUse: boolean;
  vsdPumpSpeed: number;
  isVsdPumpConnected: boolean;
  filtrationPump: string | null;
  acidPump: string | null;
  heatPump: string | null;
  cellOutput: number;
  orpTarget: number;
  tempTarget: number;
  phTarget: number;
  saltStatus: number;
  modes: any[];
  DeviceLocation: {
    address: string;
    latitude: number;
    longitude: number;
  };
  backwashDuration: number;
  boostDuration: number;
  schedules: LGPortalSchedule[];
  registered: string | null;
  token: string;

  // Keeping these for compatibility with existing code
  linkStatus: "accepted" | "pending" | "rejected";
  linkId: number | string | null;
  linkAlias?: string;
  latitude: number;
  longitude: number;
  location: string;
  /** Present only for customer-approved 72-hour dealer grants. */
  accessType?: "permanent" | "temporary";
  temporaryAccessRequestId?: number;
  temporaryAccessExpiresAt?: string | null;
  iotConnectionStatus?: {
    connected: boolean;
    timestamp: string;
  };
  shadowData: {
    ph?: number;
    orp?: number;
    salinity?: number;
    temp?: number;
    alarms?: number;
    phConnected?: boolean;
    orpConnected?: boolean;
    salinityConnected?: boolean;
    tempConnected?: boolean;
  };
}

export interface DevicesResponse {
  success: boolean;
  response: Device[];
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    limit: number;
  };
}

// LG Portal API types
export interface LGPortalSchedule {
  on: string | number;
  off: string | number;
  days: string;
  speed: string | number;
}

export interface DeviceStatusResponse {
  success: boolean;
  response: {
    device: {
      id: number;
      filtrationPump: string | null;
      cellOutput: number;
      acidPump: string | null;
      heatPump: string | null;
      orpTarget: number;
      tempTarget: number;
      phTarget: number;
      serialNumber: string;
      token: string;
      registered: string | null;
      certArn: string;
      certData: string | null;
      firmwareVersion: string;
      nipperVersion: string;
      isConnected: boolean | null;
      io: string;
      poolSize: number;
      nipperCell: number;
      brand: number;
      saltStatus: number;
      region: number;
      schedules: Array<{
        on: string;
        off: string;
        days: string;
        speed: string | number;
      }>;
      modes: any[];
      vsdPumpSpeed: number;
      isVsdPumpConnected: boolean;
      inUse: boolean;
      boostDuration: number;
      backwashDuration: number;
      createdAt: string;
      updatedAt: string;
    };
    shadow: {
      ph: number;
      orp: number;
      temp: number;
      salinity: number;
      phSetpoint: number;
      orpSetpoint: number;
      tempSetpoint: number;
      salinitySetpoint: number;
      cellOutput: number;
      setCellOutput: number;
      firmwareVersion: string;
      reqFirmwareVersion: string;
      nipperVersion: string;
      reqNipperVersion: string;
      phConnected: boolean;
      orpConnected: boolean;
      vsdSpeed: number;
      vsdIsConnected: boolean;
      salinityConnected: boolean;
      tempConnected: boolean;
      flow: number;
      nipperConnection: number;
      alarms: number;
      modes: any[];
      schedules: Array<{
        on: string;
        off: string;
        days: string;
        speed: string | number;
      }>;
      setVsd: string;
      setPost: number;
      setBoostDuration: number;
      setBackwashDuration: number;
    };
    connected: boolean;
    lastUpdatedAt: string;
  };
}

export interface StatusParameters {
  phSetpoint: number;
  orpSetpoint: number;
  tempSetpoint: number;
  salinitySetpoint: number;
  setCellOutput: number;
  // Substituindo setVsd e isVsdConnected pelas novas propriedades
  vsdSpeed: number;
  vsdIsConnected: boolean;
  phConnected: boolean;
  orpConnected: boolean;
  salinityConnected: boolean;
  tempConnected: boolean;
}

export interface DeviceShadow {
  ph: number;
  orp: number;
  temp: number;
  salinity: number;
  cellOutput: number;
  phSetpoint: number;
  orpSetpoint: number;
  tempSetpoint: number;
  salinitySetpoint: number;
  nipperConnection: number | string;
  setCellOutput: number;
  setVsd?: string; // Mantido para compatibilidade com sistemas existentes
  vsdSpeed?: number; // Nova propriedade
  vsdIsConnected?: boolean; // Nova propriedade
  phConnected: boolean;
  orpConnected: boolean;
  salinityConnected: boolean;
  tempConnected: boolean;
  flow: number;
}

export interface StatusParametersTableProps {
  shadow: DeviceShadow;
  isEditingParams: boolean;
  updatingParams: boolean;
  pendingParams: StatusParameters;
  toggleParamEditMode: () => void;
  handleSaveParams: () => void;
  handleParamChange: (param: keyof StatusParameters, value: any) => void;
  handleToggleSensorConnection: (sensorType: string) => void;
  handleEditParameter?: (paramName: string) => void;
  editingParameter: string | null;
  handleCancelEdit: () => void;
  hasDirtyParams?: boolean;
  dirtyCount?: number;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

export interface Schedule {
  on: string | number;
  off: string | number;
  days: string;
  speed: string | number;
}

export enum DeviceRegion {
  AU = "0",
  EU = "1",
}

export interface DeviceUser {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
  privledgelevel: string;
  createdAt: string;
  updatedAt: string;
}
